// The game controller: glues the SignalR transport, the velari chart and the
// panel UI together. Server-authoritative — this file never computes payouts.
import { el, find } from '../core/dom';
import { sound } from '../core/sound';
import { t } from '../core/i18n';
import { GameChart } from './chart';
import { GameTransport } from './connection';
import { GameUi } from './ui';
import type { MarketFrameDto, RoundResultDto } from './types';

export function initGame(): void {
  const host = find('gameChart');
  if (!host) return;

  const ui = new GameUi();
  const chart = new GameChart(host);
  let lastPrice = 100;
  let roundActive = false;
  let runEnded = false;
  let connected = false;

  const refreshControls = (): void => ui.setControlsEnabled(connected && !roundActive && !runEnded);

  const onResult = (result: RoundResultDto): void => {
    roundActive = false;
    runEnded = result.runEnded;
    chart.clearEntry();
    ui.stopCountdown();
    ui.setStats(result.balance, result.wins, result.losses, result.streak);
    ui.showResult(result);
    if (result.isWin) sound.win();
    else sound.lose();
    if (result.challengeCompleted || result.runEnded) ui.openGameOver(result);
    refreshControls();
  };

  const onFrame = (frame: MarketFrameDto): void => {
    if (frame.seed) {
      chart.reset(frame.seed);
      connected = true;
      ui.setStatus('live', 'live');
    }
    if (frame.session) {
      ui.applySession(frame.session);
      runEnded = frame.session.runEnded;
    }
    if (frame.round) {
      roundActive = true;
      chart.markEntry(frame.round.direction, frame.round.entryPrice);
      ui.startCountdown(frame.round.deadline, frame.now);
    }
    chart.tick(frame);
    ui.setPrice(frame.close, lastPrice);
    lastPrice = frame.close;
    if (frame.result) onResult(frame.result);
    refreshControls();
  };

  const transport = new GameTransport({
    onFrame,
    onStreamDown: () => {
      connected = false;
      ui.setStatus('error', 'offline');
      refreshControls();
    },
  });

  const startRound = async (direction: 'buy' | 'sell'): Promise<void> => {
    if (!connected || roundActive || runEnded) return;
    sound.click();
    const round = await transport.startRound(direction);
    if (!round) return;
    roundActive = true;
    chart.markEntry(round.direction, round.entryPrice);
    ui.startCountdown(round.deadline, round.startedAt);
    refreshControls();
  };

  // ----- wiring -----------------------------------------------------------------

  ui.initModals();
  el('buyBtn').addEventListener('click', () => void startRound('buy'));
  el('sellBtn').addEventListener('click', () => void startRound('sell'));
  el('historyBtn').addEventListener('click', () => ui.openHistory());
  find('achievementsBtn')?.addEventListener('click', () => ui.openAchievements());
  find('leaderboardBtn')?.addEventListener('click', () => void ui.openLeaderboard());
  find('saveScoreBtn')?.addEventListener('click', () => ui.openSaveScore(currentBalance()));
  find('gameOverSave')?.addEventListener('click', () => ui.openSaveScore(currentBalance()));
  find<HTMLFormElement>('saveScoreForm')?.addEventListener('submit', (e) => {
    e.preventDefault();
    void ui.submitScore();
  });
  find('playAgainBtn')?.addEventListener('click', () => {
    void transport.resetRun().then((session) => {
      ui.applySession(session);
      runEnded = false;
      el('gameOverModal').hidden = true;
      refreshControls();
    });
  });

  find<HTMLSelectElement>('difficultySelect')?.addEventListener('change', (e) => {
    sound.click();
    void transport.setDifficulty((e.target as HTMLSelectElement).value).then((s) => ui.applySession(s));
  });
  find<HTMLSelectElement>('gameModeSelect')?.addEventListener('change', (e) => {
    sound.click();
    void transport.setMode((e.target as HTMLSelectElement).value).then((session) => {
      ui.applySession(session);
      runEnded = session.runEnded;
      refreshControls();
    });
  });

  const soundToggle = find('soundToggle');
  const paintSound = (): void => {
    soundToggle?.classList.toggle('is-off', !sound.enabled);
  };
  soundToggle?.addEventListener('click', () => {
    sound.toggle();
    paintSound();
  });
  paintSound();

  document.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
    const key = e.key.toLowerCase();
    if (key === 'b' || key === '\u0438') void startRound('buy');
    else if (key === 's' || key === '\u044B') void startRound('sell');
    else if (key === 'h' || key === '\u0440') ui.openHistory();
  });

  // Pause the feed when the tab hides; the server keeps settling open rounds,
  // and the fresh seed frame on resume reconciles the dashboard.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && transport.streaming) {
      transport.pause();
      ui.setStatus('paused', 'paused');
    } else if (!document.hidden && !transport.streaming) {
      ui.setStatus('connecting', 'connecting');
      transport.subscribe();
    }
  });

  const currentBalance = (): number => Number.parseFloat(el('balance').textContent ?? '0') || 0;

  ui.setStatus('connecting', 'connecting');
  transport.start().catch(() => {
    ui.setStatus('error', 'offline');
    el('gameStatusText').textContent = t('offline');
  });
}
